/**
 * 기사관리 회원목록 DataTable 공용 모듈 — $j.F.Admin.Member.ListTable
 *
 * /adm/members(Pages/Admin/MemberList)와 기사검색 모달(SearchModal.js)이 공유한다.
 * 컬럼 렌더링·서버사이드 ajax(POST /adm/ajax/members)·rowCallback 을 한 곳에서 정의하고,
 * 호스트는 "표시할 컬럼 key 목록"만 골라 넘긴다. (렌더 로직은 동일, 나오는 컬럼만 조절)
 *
 * 사용:
 *   const $table = $j.F.Admin.Member.ListTable.create({
 *       selector: '#table',
 *       getSearch: () => SEARCH,     // ajax 파라미터로 병합할 검색조건 객체
 *       pageLength: 50,
 *       appVer,                      // 버전컬럼 '최신' 판정 기준 (없으면 window.appVer)
 *   });
 *   // 컬럼 일부만: columns: ['no','carType','name','phone','status']
 *
 * 렌더가 참조하는 것: Util(import),
 *   $j.F.Admin.Util (memberPortal/memberEdit/chat/userOrder/userCash/memberDocEdit/penaltyCard).
 */
import Util from '../../../Common/Util';

export default class ListTable {

    /**
     * 컬럼 표시 순서 기본값 (/adm/members).
     *
     * **-6차는 컬럼에서 뺐다** — 회원 팝오버(`Member.Pop`)가 같은 값을 이미 보여주고,
     * 자세한 이력은 기사편집의 「-6차」 가 연다. 표에 한 칸을 더 두는 값어치가 없다.
     *
     * **소유자 칸도 뺐다** — 열의 90%가 '소유자' 라는 같은 글자였다. 드문 쪽(소속자)만
     * 이름 옆 아이콘으로 말하고, 소유자에게 건너가는 길은 회원 팝오버가 갖는다.
     *
     * **톡 칸은 남긴다** — 팝오버에도 불러톡이 있지만 CS 가 가장 자주 누르는 버튼이라
     * 한 번 더 누르게 만들면 손이 느려진다.
     */
    static get DEFAULT_ORDER() {
        return ['no', 'carType', 'bonsa', 'gisa', 'phone', 'name', 'cash',
                'addr', 'car', 'talk', 'counter', 'soo', 'version', 'status', 'doc', 'login'];
    }

    /** 칸 전체가 회원 팝오버 버튼인 컬럼 (업종·본사·지사·캐시·지역·기종 — 링크가 없는 칸들) */
    static get POP_CELLS() {
        return 'td.ml-type, td.ml-bonsa, td.ml-gisa, td.ml-cash, td.ml-addr, td.ml-car';
    }

    /**
     * 전체 컬럼 정의 (key → DataTables column def). 렌더는 원본 그대로.
     * @param {object} opts { appVer }
     */
    static columns(opts = {}) {
        const appVer = (opts.appVer !== undefined && opts.appVer !== null)
            ? opts.appVer
            : (typeof window.appVer !== 'undefined' ? window.appVer : 0);

        return {
            // 회원번호를 누르면 그 행에서 갈 수 있는 곳이 한 자리에 펼쳐진다.
            // 예전에는 액션이 셀마다 흩어져 있어(업종=정보요약, 핸드폰=앱화면, 이름=편집, 톡=불러톡,
            // 서류=심사) 어디를 눌러야 뭐가 뜨는지 외워야 했고, 목록에서 아예 닿지 못하는 것도
            // 있었다(기사눈 서류화면·캐시내역 — 편집창까지 들어가야 했다).
            // 셀 링크는 그대로 둔다 — 손에 굳은 동선이라 메뉴는 '추가' 경로다.
            no: {
                data: 'num', width: '50px', title: 'No', className: 'ml-no',
                render: function (data, type, row) {
                    return `<a href="javascript:void(0)" class="btn-mb-actions" data-mb-no="${row.num}">${data}</a>`;
                }
            },

            carType: {
                data: 'mem_car_type', width: '80px', title: '업종', className: 'ml-type',
                render: function (data, type, row) {
                    if (row.mem_driver_type == '수발주자') return `<b class='ml-t--owner'>${Util.nvl(data)}</b>`;
                    return `<b style='color:var(--adm-kw-red, red)'>${Util.escapeHtml(row.mem_driver_type)}</b>`;
                }
            },

            bonsa: {
                data: null, width: '40px', title: '본사', className: 'ml-bonsa',
                render: function (data, type, row) {
                    return `<span class='${row._bonsaCls}'>${row._bonsaName}</span>`;
                }
            },

            gisa: {
                data: null, width: '100px', title: '지사', className: 'ml-gisa',
                render: function (data, type, row) {
                    return `${row._gisaName}`;
                }
            },

            phone: {
                data: 'mem_phone', width: '115px', title: '핸드폰', className: 'ml-phone',
                render: function (data, type, row) {
                    let col = 'var(--adm-kw-darkblue, darkblue)';
                    return `<a href='javascript:$j.F.Admin.Util.userOrder(${row.num})'><b style='color:${col}'>${Util.escapeHtml(data)}</b></a>`;
                }
            },

            name: {
                data: 'mem_name', width: '100px', title: '이름', className: 'ml-name',
                render: function (data, type, row) {
                    // 제재 딱지 — ycard = g5_member_owners.penalty 하나만 본다.
                    // 환원페널티(3)까지 이 컬럼이 흡수해서 행마다 붙던 서브쿼리를 없앴다
                    // (2026-08 — .ai/domains/penalty.md)
                    // 경고(1)·환원(3)은 딱지 대신 칸 앞에 노란·빨간 띠 (createdCell) — 접수목록(F/Order/ListTable)과 같다
                    const band = { 1: 'ml-cell--warn', 3: 'ml-cell--whan' }[Util.int(row.ycard)];
                    const redCard = band ? '' : $j.F.Admin.Util.penaltyCard(row.ycard, ' ');
                    let aStr = ''; if (row._isAdmin == 1) aStr = `<b style='color:var(--adm-kw-red, red)'>[관]</b>`;
                    // **소속자는 이름을 눌러도 편집창이 아니라 회원 팝오버가 뜬다.**
                    // 소속자를 볼 때 하려는 일은 대개 본인 편집이 아니라 "누구 밑인가 · 소유자로
                    // 건너가기" 라서, 편집창이 바로 뜨면 닫고 다시 번호를 눌러야 했다.
                    // 회원번호(No)와 같은 앵커라 위임도 그대로 탄다(btn-mb-actions).
                    const isSub = row.mem_permit === '소속자';
                    // 탈퇴신청(98)/탈퇴(99) 회원은 포털 대신 회원편집으로 연다
                    const open = (Number(row.mem_status) >= 98)
                        ? `$j.F.Admin.Util.memberEdit(${row.num})`
                        : `$j.F.Admin.Util.memberPortal(${row.num})`;
                    const anchor = isSub
                        ? `href="javascript:void(0)" class="btn-mb-actions" data-mb-no="${row.num}"`
                        : `href='javascript:${open}'`;
                    // 소속자 표식 — **소속자일 때만** 붙는다. 소유자 칸을 따로 두면 열의 90%가
                    // '소유자' 라는 같은 글자로 차서, 정작 드문 쪽(소속자)이 묻혔다.
                    // 눌러도 이름과 같은 곳(본인 편집)으로 간다 — 소유자로 가는 길은 팝오버에 있다.
                    // `[소]` 는 소유자권한(is_owner_auth)이 붙은 소속자다 — 뜻이 다른 표식이라 남긴다.
                    const sosok = isSub
                        ? ` <i class="fas fa-user-group ml-tag-sosok" title="소속자${row.mem_ownercode ? ' · 소유자 #' + row.mem_ownercode : ''}"></i>`
                          + (row.is_owner_auth == 1 ? `<b class="ml-tag-auth" title="소유자권한">소</b>` : '')
                        : '';
                    // 소속자는 `소유자(소속자)` 로 보인다 — DB 에는 소속자 실명만 있고 서버가 붙여 준다(_name)
                    return `<a ${anchor}>${redCard}<b style='color:var(--adm-kw-darkblue, darkblue)'>${Util.escapeHtml(row._name || data)}</b> ${aStr}${sosok}</a>`;
                },
                createdCell: function (td, cellData, rowData) {
                    $(td).addClass({ 1: 'ml-cell--warn', 3: 'ml-cell--whan' }[Util.int(rowData.ycard)] || '');
                }
            },

            cash: {
                data: 'mem_mycashe', width: '90px', title: '캐시', className: 'ml-cash',
                render: function (data, type, row) {
                    // 캐시는 **소유자 전유물**이다 — 소속자 줄은 비워 둔다.
                    // '-' 로 채우면 눈이 그 자리를 계속 훑는다(값이 없다는 것을 매번 다시 읽는다).
                    // 같은 기준으로 회원 팝오버도 소속자에게는 캐시 줄을 아예 안 낸다(Member.Pop).
                    if ($j.F.Admin.Member.Pop.cashLeft(row) === null) return '';
                    // 회비결제 — 실제로 받는 길 하나만(서버 MemberFeeService::wayOf, 기사편집창과 같은 판정). 무료회원 · 충전금은 없음.
                    // 설정값(CMS 번호 · 선결제 · 감면)마다 따로 붙이면 「C 감」처럼 둘이 떠서 어느 쪽으로 받는지 알 수 없었다.
                    // 타업종을 샀으면 +N. 마우스를 올리면 기사편집창과 같은 한 줄
                    // 충전금(기본 방식)은 아이콘을 달지 않는다 — 유료회원 대부분이 붙으면 다른 표시가 묻힌다. 타업종 +N 은 그대로
                    const FEE = { cms: ['cms', 'C'], prepay: ['pre', '선'], nofee: ['nofee', '감'], exempt: ['nofee', '면'] };
                    // 무료회원이라도 CMS 번호가 있으면 C 는 붙인다 — 청구는 없지만 약정이 걸려 있다는 표시
                    const freeCms = row._feeWay === 'free' && String(row.cms_num || '') !== '';
                    const f = FEE[row._feeWay] || (freeCms ? FEE.cms : null);
                    const ext = Number(row._feeExtra) > 0 ? `<span class='bx-badge-extra'>+${Number(row._feeExtra)}</span>` : '';
                    const tip = $('<i>').text((row._feeText || '') + (freeCms ? ' (CMS 번호 있음)' : '')).html();
                    const ccs = f || ext ? ` <span data-tip-hover data-tip="${tip}">${f ? `<span class='bx-badge-${f[0]}'>${f[1]}</span>` : ''}${ext}</span> ` : '';
                    return `${ccs}${Util.formatNumber($j.F.Admin.Member.Pop.cashLeft(row))}`;
                }
            },

            addr: {
                data: null, width: '132px', title: '지역', className: 'ml-addr',
                render: function (data, type, row) {
                    return Util.escapeHtml(row._addr);
                }
            },

            car: {
                // **가변칸이다** (`width:'*'`) — 다른 컬럼이 쓰고 남은 폭을 받는다.
                // 다만 내용이 한 줄로 고정되면 남는 폭이 없을 때 표를 밖으로 밀어낸다.
                // 그래서 접히게 둔다(td.ml-car) — 그러면 이 칸이 화면에 맞춰 줄었다 늘었다 한다.
                data: null, width: '*', title: '기종', className: 'ml-car',
                render: function (data, type, row) {
                    return `${row._car}`;
                }
            },

            talk: {
                data: null, width: '45px', title: '톡', className: 'ml-talk',
                render: function (data, type, row) {
                    if (row['mobie_ver'] < 2) return '';
                    // 이름은 JS 문자열(onclick) 안에 들어가므로 JSON 으로 감싼 뒤 속성 이스케이프한다
                    const nm = Util.escapeHtml(JSON.stringify(String(row['mem_name'] ?? '')));
                    return `<button onclick="$j.F.Admin.Util.chat(${Util.int(row['num'])},${nm})"><i class="fab fa-rocketchat"></i>톡</button>`;
                }
            },

            counter: {
                data: null, width: '40px', title: '차수', className: 'ml-ctr',
                render: function (data, type, row) {
                    const conter = $j.F.Admin.Member.Pop.counterOf(row);
                    let cls = 'w3-badge-gray';
                    if (conter != 0) cls = 'w3-badge-red';
                    return `<span class='${cls}'>${conter}</span>`;
                }
            },

            soo: {
                data: null, width: '50px', title: '수주', className: 'ml-soo',
                render: function (data, type, row) {
                    const sooCnt = $j.F.Admin.Member.Pop.sooCnt(row);
                    let cls = 'w3-badge-gray';
                    if (sooCnt > 0) cls = 'w3-badge-green';
                    return `<span class='${cls}'>${sooCnt}</span>`;
                }
            },

            version: {
                data: null, width: '60px', title: '버전', className: 'ml-ver',
                render: function (data, type, row) {
                    let ver = row['mobie_ver'];
                    if (ver == null || ver === '') return '';
                    const isIos = row['mobile_type'] === 'ios';
                    // 아이폰 기준이 0(미설정)이면 판정하지 않고 번호를 그대로 보인다
                    const latest = isIos ? $j.F.Admin.Member.Pop.appVer(row) : appVer;
                    if ((!isIos || parseFloat(latest) > 0) && ver >= latest) return '<span style="color:var(--adm-kw-blue, blue)">최신</span>';
                    if (ver < 2.29) return `<span style="color:var(--adm-kw-red, red)">${ver}</span>`;
                    return `${ver}`;
                }
            },

            status: {
                data: null, width: '50px', title: '상태', className: 'ml-status',
                render: function (data, type, row) {
                    return `${row['_status']}`;
                }
            },

            doc: {
                data: null, width: '50px', title: '서류', className: 'ml-doc',
                render: function (data, type, row) {
                    let bgclss = 'w3-badge-gray';
                    if (row['_doc_max'] != row['_doc_cnt']) bgclss = 'w3-badge-noti';
                    return `<a href='javascript:$j.F.Admin.Util.memberDocEdit(${row.num})'><span class='${bgclss}'>${row['_doc_cnt']}</span></a>`;
                }
            },

            login: {
                data: null, width: '100px', title: '로그인', className: 'ml-login',
                render: function (data, type, row) {
                    return `${row._loginDate}`;
                }
            },
        };
    }

    /**
     * 회원목록 DataTable 생성.
     * @param {object} o {selector, columns:[key], getSearch:()=>obj, pageLength=50, appVer}
     * @returns DataTable 인스턴스
     */
    static create(o = {}) {
        this._injectStyle();

        this._appVer = (o.appVer !== undefined && o.appVer !== null)
            ? o.appVer : (typeof window.appVer !== 'undefined' ? window.appVer : 0);
        const defs = this.columns({ appVer: this._appVer });
        const keys = (o.columns && o.columns.length) ? o.columns : this.DEFAULT_ORDER;
        const columns = keys.map((k) => defs[k]).filter(Boolean);

        const getSearch = (typeof o.getSearch === 'function') ? o.getSearch : () => ({});

        // 공통 폰트/셀 스타일을 한 곳(_injectStyle)에서 관리하기 위한 표식 클래스
        $(o.selector).addClass('member-list-table');

        // 같은 셀렉터에 이미 DataTable 이 붙어 있으면 파기 (모달 재오픈 등 재초기화 방어)
        if ($.fn.DataTable.isDataTable(o.selector)) {
            try { $(o.selector).DataTable().destroy(); } catch (e) { /* noop */ }
        }


        const dt = new DataTable(o.selector, {
            columns,
            rowCallback: function (row, data) {
                if (data.mem_status == 99) $(row).addClass('gray-row');          // 탈퇴자 회색
                else if (data.mem_status == 1 || data.mem_status == 2) $(row).addClass('gray-row-light'); // 대기·휴면 연회색
            },
            ajax: {
                url: '/adm/ajax/members',
                type: 'post',
                data: function (d) { Object.assign(d, getSearch()); },
            },
            processing: true,
            serverSide: true,
            ordering: false,
            pageLength: (o.pageLength != null) ? o.pageLength : 50,
            lengthChange: false,
            searching: false,
        });

        // 회원번호 앵커 — 호스트(목록·검색모달)가 각자 걸지 않게 여기서 건다.
        // tbody 는 DataTables 가 갈아끼우므로 table 요소에 위임한다.
        $(o.selector).off('click.mbno').on('click.mbno', '.btn-mb-actions', function (e) {
            e.preventDefault();
            const row = dt.row($(this).closest('tr')).data();
            if (row) $j.F.Admin.Member.Pop.open(this, row);
        });

        // 링크가 없는 칸(업종·본사·지사·캐시·지역·기종)은 **칸 아무 데나** 눌러도 같은 팝오버가 열린다.
        // 이 넷은 글자만 있어서 '누를 데'가 없었고, 그때마다 회원번호까지 마우스를 되돌려야 했다.
        // 앵커·버튼이 든 칸(이름·핸드폰·톡·서류)은 각자 제 동작이 있으므로 대상이 아니다.
        $(o.selector).off('click.mbcell').on('click.mbcell', ListTable.POP_CELLS, function (e) {
            if ($(e.target).closest('a, button').length) return;   // 칸 안의 링크는 제 동작 우선
            if (String(window.getSelection())) return;             // 값 드래그 복사 중이면 안 연다
            const row = dt.row($(this).closest('tr')).data();
            if (row) $j.F.Admin.Member.Pop.open(this, row);
        });

        return dt;
    }

    /**
     * 컬럼 렌더가 쓰는 뱃지/행 스타일 주입. 1회만 주입(id 가드).
     * /adm/members 외의 페이지 위에 뜬 모달에서도 본사/캐시/차수 등 뱃지가 보이도록 하되,
     * 모든 규칙을 table.member-list-table 안으로 스코프해 호스트 페이지 요소엔 영향 없음.
     */
    static _injectStyle() {
        if (document.getElementById('member-listtable-style')) return;
        const css = `
/* 회원목록 표 공통 (풀페이지·모달 단일 관리) — 테두리/헤더/셀 여백 + 폰트 클램프.
   _base.scss 기본 14px 를 셀·자식(<b>/<a>)까지 12px 로 눌러, 모달이 다른 페이지 위에서 커 보이지 않게 한다. */
table.member-list-table{border-collapse:collapse!important;border:1px solid #444!important}
table.member-list-table th,table.member-list-table td{border:1px solid #444!important}
table.member-list-table thead th{background:#004080!important;color:#fff!important;font-weight:normal;text-align:center;padding:0!important;line-height:40px!important;border:none!important}
/* DataTables 의 columns.className 이 th 에도 .text-right 를 붙여 헤더가 우측 정렬되는 문제 — th 는 가운데 정렬 강제 */
table.member-list-table thead th.text-right{text-align:center!important}
/* 수발주자 라벨 — 예전엔 인라인 #004080 이었다(다크에서 어두운 바탕에 남색). */
.ml-t--owner{color:#004080}
/* ── 다크 — 색상(hue)은 두고 명도만 올린다 ── */
html[data-theme="dark"] .ml-t--owner{color:#8ab4f8}
html[data-theme="dark"] .bx-badge-gray,
html[data-theme="dark"] .w3-badge-gray{background:#6c757d!important}
table.member-list-table td{padding:5px!important;line-height:22px!important;vertical-align:middle!important}
/* 이름 — 경고(ycard 1)·환원페널티(3)는 딱지 대신 칸 왼쪽 16px 노란·빨간 띠. 접수목록과 같은 값.
   좌우 여백을 띠 폭으로 똑같이 둬서 띠 유무와 상관없이 모든 이름이 칸 가운데 같은 자리에 선다 */
table.member-list-table td.ml-name{padding-left:16px!important;padding-right:16px!important}
/* 띠는 box-shadow 가 아니라 배경 그림으로 — DataTables 줄무늬가 td 의 box-shadow(inset 9999px)를 덮어쓴다 */
table.member-list-table td.ml-cell--warn{background-image:linear-gradient(to right,#FFE680 16px,transparent 16px)!important}
html[data-theme="dark"] table.member-list-table td.ml-cell--warn{background-image:linear-gradient(to right,#8a7a2a 16px,transparent 16px)!important}
table.member-list-table td.ml-cell--whan{background-image:linear-gradient(to right,#FF6B6B 16px,transparent 16px)!important}
html[data-theme="dark"] table.member-list-table td.ml-cell--whan{background-image:linear-gradient(to right,#9a3434 16px,transparent 16px)!important}
table.member-list-table thead th,table.member-list-table thead th *,
table.member-list-table tbody td,table.member-list-table tbody td *{font-size:12px}
/* 표 안 네이티브 버튼(톡 등) 테두리 얇게 — 모달에서도 동일 (AdminLTE3+BS4 기본 굵은 테두리 방지) */
/* 바탕을 정하면 글자색도 **쌍으로** 정한다 — 없으면 다크에서 상속 글자(밝은색)가
   흰 버튼 위로 올라와 '흰 사각형'이 된다(실제로 톡 버튼이 그랬다). */
table.member-list-table td button:not([class*="btn"]){border:1px solid var(--adm-field-border,#999);padding:1px 5px;background:var(--adm-field-bg,#fff);color:var(--adm-text,#212529);border-radius:3px;white-space:nowrap;cursor:pointer}

/* ── 회원목록 뱃지 시스템 (table.member-list-table 안으로만 스코프 — 호스트 페이지 누수 방지) ─
   3계열: pill 태그(본사/구분/-6차) · 초소형 상태태그(캐시 옆) · 카운트 원형(차수/수주/서류) */

/* 칩 — 본사/구분.
   모양은 **테마가 정한다**(--adm-radius-chip): 기본은 각지게, 부드럽게는 알약.
   각진 화면에 알약만 떠 있으면 그 줄이 혼자 다른 시대의 것으로 보인다.
   폴백 999px 은 토큰이 없는 화면(모달 위 목록 등)에서 지금 모양을 지키기 위한 것이다 */
table.member-list-table .bx-badge-gray,table.member-list-table .bx-badge-a,table.member-list-table .bx-badge-b,table.member-list-table .bx-badge-c,
table.member-list-table .bx-badge-ss,table.member-list-table .bx-badge-lg,table.member-list-table .bx-badge-all,table.member-list-table .bx-badge-jb{
  display:inline-block;padding:1px 8px;border-radius:var(--adm-radius-chip, 999px);
  font-size:11px;font-weight:600;line-height:1.6;letter-spacing:-.2px;
  color:#fff;white-space:nowrap;vertical-align:middle}
table.member-list-table .bx-badge-gray{background:#aaadab}
table.member-list-table .bx-badge-a{background:#154bd5}
table.member-list-table .bx-badge-b{background:#3a3c3d}
table.member-list-table .bx-badge-c{background:#3a3c3d}
table.member-list-table .bx-badge-ss{background:#358ee7}
table.member-list-table .bx-badge-lg{background:#d91254}
table.member-list-table .bx-badge-all{background:#ad1a07}
table.member-list-table .bx-badge-jb{background:#ec8a28}

/* 팝오버가 열리는 칸(업종·본사·지사·캐시·지역·기종) — 링크가 없어 눌러도 되는지 알 수 없으니 커서로 말한다 */
table.member-list-table td.ml-type,table.member-list-table td.ml-bonsa,
table.member-list-table td.ml-gisa,table.member-list-table td.ml-cash,
table.member-list-table td.ml-addr,table.member-list-table td.ml-car{cursor:pointer}

/* 기종칸 — 남는 폭을 받는 가변칸(width:'*'). 폭을 못박지 않는다.
   대신 **접히게** 둔다: 한 줄로 고정되면 화면이 좁을 때 표가 가로로 밀려난다 */
table.member-list-table td.ml-car{white-space:normal;word-break:break-all;line-height:1.45}

/* 소속자 표식 — 이름 옆. 이름을 가리지 않게 한 단 작고 흐리다.
   소유자권한('소')은 뜻이 다른 표식이라 따로 붙는다 */
table.member-list-table .ml-tag-sosok{font-size:10px;color:#8a8f94;vertical-align:middle}
table.member-list-table .ml-tag-auth{
  display:inline-block;margin-left:2px;padding:0 3px;border-radius:4px;
  background:#6c757d;color:#fff;font-size:9px;font-weight:700;line-height:1.5;vertical-align:middle}

/* 초소형 상태태그 — 캐시 옆 회비결제 하나: CMS(C)/선결제(선)/감면(감·면) + 타업종(+N). 충전금은 표시 없음 */
table.member-list-table .bx-badge-cms,table.member-list-table .bx-badge-pre,table.member-list-table .bx-badge-nofee,
table.member-list-table .bx-badge-extra{
  display:inline-block;padding:1px 5px;border-radius:5px;
  font-size:9px;font-weight:700;line-height:1.5;color:#fff;vertical-align:middle}
table.member-list-table .bx-badge-cms{background:#9c6464}
table.member-list-table .bx-badge-pre{background:#6d8dad}
table.member-list-table .bx-badge-nofee{background:#3a3c3d}
table.member-list-table .bx-badge-extra{background:#d9822b;margin-left:2px}

/* 카운트 원형 — 차수/수주/서류 */
table.member-list-table .w3-badge-gray,table.member-list-table .w3-badge-noti,table.member-list-table .w3-badge-green,table.member-list-table .w3-badge-red,table.member-list-table .w3-badge-sky{
  display:inline-flex;align-items:center;justify-content:center;
  min-width:19px;height:19px;padding:0 5px;box-sizing:border-box;
  border-radius:var(--adm-radius-chip, 999px);font-size:11px;font-weight:700;line-height:1;color:#fff}
table.member-list-table .w3-badge-gray{background:#aaadab}
table.member-list-table .w3-badge-noti{background:#000000}
table.member-list-table .w3-badge-green{background:#17a106}
table.member-list-table .w3-badge-red{background:#e10a4b}
table.member-list-table .w3-badge-sky{background:#358ee7}

/* 행 상태 (탈퇴/휴면) — member-list-table 로만 한정 */
table.member-list-table tr.gray-row{color:gray!important;background:#e0e0e0}
table.member-list-table tr.gray-row *{color:gray!important}
table.member-list-table tr.gray-row-light{color:#999!important;background:#f0f0f0}
table.member-list-table tr.gray-row-light *{color:#999!important}

/* 회원정보 요약 모달 표 */
table.tab1{width:95%;margin:0 auto}
table.tab1 td,table.tab1 th{border:1px solid #929292;border-collapse:collapse;font-size:10pt}
table.tab1 th{font-weight:bold;color:#fff}
`;
        const el = document.createElement('style');
        el.id = 'member-listtable-style';
        el.textContent = css;
        document.head.appendChild(el);
    }

}
